/*
 * Module for handling the properties of a response (belonging to a conversation node,
 * branching or not).
 */

define([
  "backbone",
  "marionette",
  "messaging/messenger",
  "moment",
  "./topicTasksView",
  "modules/tasks/tasksList/tasksListModule",
  "modules/tasks/tasksArea/tasksAreaModule",
  "./topicTasksListToggle/topicTasksListToggleModule",
  "modules/tasks/taskWizardModal/taskWizardModalModule",
  "modules/tasks/tasksModel",
], function (
  Backbone,
  Marionette,
  Messenger,
  Moment,
  TopicTasksView,
  TasksListModule,
  TasksAreaModule,
  TasksListToggleModule,
  TaskWizardModalModule,
  TaskModel,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "topicTasks";
      this.region = options.region;
      this.topic = options.topic;
      this._setUpVents();
    },
    show: function (tasks) {
      //keep the default language and touchpoint
      const defaultTouchpoint = Messenger.reqres.request("default:touchpoint");
      const defaultLanguage = Messenger.reqres.request("default:response:language");
      const defaultDueDate = 7; //number of days ahead to schedule the task by default
      const defaultInterval = TaskModel.INTERVAL.YEARLY; //default to yearly
      const canCreateTask = Messenger.reqres.request("can:create:task");

      this.tasks = tasks;
      this.layout = new TopicTasksView.topicTasksLayout({
        model: new Backbone.Model({
          canCreateTask: canCreateTask,
        }),
      });
      this.list = this.addSubModule(new TasksListModule({ region: this.layout.list }));
      this.area = this.addSubModule(new TasksAreaModule({ region: this.layout.area }));
      this.listToggle = this.addSubModule(
        new TasksListToggleModule({ region: this.layout.listToggle }),
      );
      //display how many tasks there are once they are synced
      tasks.onSynced(function () {
        Messenger.vent.trigger("update:task:count", tasks.getDisplayTasksCount());
        //don't want to start listening to collection changes until the whole tasks collection is fully synced
        tasks.on(
          "change remove add",
          function () {
            Messenger.vent.trigger("update:task:count", tasks.getDisplayTasksCount());
          },
          this,
        );
      }, this);
      //create a new topic review and add it to the list
      this.layout.on(
        "new:topic:review",
        function () {
          const userName = Messenger.reqres.request("user").get("userName");
          //make sure that the tasks collection is synced first
          tasks.onSynced(function () {
            const newTask = tasks.addTask({
              type: TaskModel.TYPE.TOPIC_REVIEW,
              intentId: this.topic.id,
              dueDate: Moment.utc().add(defaultDueDate, "days").toDate(),
              creator: userName,
              scheduled: false,
              interval: defaultInterval,
              hasExpiration: false,
              expirationDate: null,
            });
            //trigger a special "selected" event to notify the task list to select this task
            newTask.trigger("selected");
          }, this);
          //Show Not Started tasks whenever a new one is created
          Messenger.vent.trigger("show:tasks:list", TaskModel.STATUS.NOT_STARTED);
          //Also manually select the "Not Started" button
          this.listToggle.selectTab(TaskModel.STATUS.NOT_STARTED);
        },
        this,
      );

      this.layout.on(
        "new:update:response",
        function () {
          const userName = Messenger.reqres.request("user").get("userName");
          //make sure that the tasks collection is synced first
          tasks.onSynced(function () {
            const newTask = tasks.addTask({
              type: TaskModel.TYPE.UPDATE_RESPONSE,
              languageId: defaultLanguage,
              touchpointId: defaultTouchpoint,
              intentId: this.topic.id,
              dueDate: Moment.utc().add(defaultDueDate, "days").toDate(),
              creator: userName,
              scheduled: false,
              interval: defaultInterval,
              hasExpiration: false,
              expirationDate: null,
            });
            //trigger a special "selected" event to notify the task list to select this task
            newTask.trigger("selected");
          }, this);
          //Show Not Started tasks whenever a new one is created
          Messenger.vent.trigger("show:tasks:list", TaskModel.STATUS.NOT_STARTED);
          //Also manually select the "Not Started" button
          this.listToggle.selectTab(TaskModel.STATUS.NOT_STARTED);
        },
        this,
      );

      this.layout.on(
        "new:translate:response",
        function () {
          const userName = Messenger.reqres.request("user").get("userName");
          //make sure that the tasks collection is synced first
          tasks.onSynced(function () {
            const newTask = tasks.addTask({
              type: TaskModel.TYPE.TRANSLATE_RESPONSE,
              languageId: defaultLanguage,
              targetLanguageId: defaultLanguage,
              touchpointId: defaultTouchpoint,
              intentId: this.topic.id,
              dueDate: Moment.utc().add(defaultDueDate, "days").toDate(),
              creator: userName,
              scheduled: false,
              interval: defaultInterval,
              hasExpiration: false,
              expirationDate: null,
            });
            //trigger a special "selected" event to notify the task list to select this task
            newTask.trigger("selected");
          }, this);
          //Show Not Started tasks whenever a new one is created
          Messenger.vent.trigger("show:tasks:list", TaskModel.STATUS.NOT_STARTED);
          //Also manually select the "Not Started" button
          this.listToggle.selectTab(TaskModel.STATUS.NOT_STARTED);
        },
        this,
      );

      this.layout.on("new:advanced:task", this._onNewAdvancedTask, this);

      this.layout.on(
        "show",
        function () {
          this.listToggle.show();
          this.list.show(tasks, TaskModel.STATUS.NOT_STARTED, true); //default to the 'not started' status
        },
        this,
      );
      this.showRegion(this.layout);
    },
    _onNewAdvancedTask: function () {
      const task = Messenger.reqres.request("task:createAdvanced");
      task.set("intentId", this.topic.id);
      task.set("intentName", this.topic.get("intentName"));
      Messenger.vent.trigger("show:modal", TaskWizardModalModule, {}, task, {
        closeButton: false,
        closeOnBackgroundClick: false,
        title: "task.wizard.title",
        trackInteraction: {
          action: "Add Advanced Task",
          id: this.topic.id,
          idType: "Topic",
          object: "Topic",
          source: "Topic",
        },
      });
    },
    _setUpVents: function () {
      //show the tasks list with a specific status Id
      Messenger.vent.on(
        "show:tasks:list",
        function (statusId) {
          this.list.show(this.tasks, statusId, true);
        },
        this,
      );
      Messenger.vent.on(
        "show:resubmitted:task",
        function (task) {
          this.tasks.add(task);
        },
        this,
      );
      Messenger.vent.on(
        "show:no:task",
        function () {
          this.layout.resetTaskArea();
        },
        this,
      );
    },
  });
});
